import * as React from 'react';
import { createContext, useContext, useEffect, useState } from 'react';
import type { Dictionary } from 'lodash';
import {
   DevicePickerContextType,
   DevicePickerProps,
   Hierarchy,
   State,
   SearchState,
   View,
   SEARCH_INACTIVE,
   SEARCH_PENDING,
   HIDE_GRID_VIEW,
} from './types';
import findAllLeaves from './utils/findAllLeaves';

/* eslint-disable @typescript-eslint/no-empty-function */
const devicePickerContextDefaults: DevicePickerContextType = {
   state: 'category',
   query: '',
   category: '',
   leaves: null,
   path: [],
   searchState: SEARCH_INACTIVE,
   view: HIDE_GRID_VIEW ? View.Column : View.Grid,
   loaded: false,
   setState: () => {},
   setQuery: () => {},
   setCategory: () => {},
   setPath: () => {},
   setSearchState: () => {},
   setView: () => {},
   updateCategory: () => {},
   setLoaded: () => {},
   isUncategorized: () => false,
};

export const DevicePickerContext = createContext<DevicePickerContextType>(
   devicePickerContextDefaults,
);
export const useDevicePickerContext = () => useContext(DevicePickerContext);

export const withDevicePickerContext = (Component: any) => {
   return function WithDevicePickerContextComponent(props: DevicePickerProps) {
      const [state, setState] = useState<State>('category');
      const [query, setQuery] = useState<string>('');
      const [category, setCategory] = useState<string>('');
      const [hierarchy, setHierarchy] = useState<Hierarchy>();
      const [displayTitles, setDisplayTitles] = useState<Dictionary<string>>();
      const [leaves, setLeaves] = useState<Set<string> | null>(null);
      const [path, setPath] = useState<string[]>([]);
      const [searchState, setSearchState] = useState<SearchState>(
         props.initialDevice ? SEARCH_PENDING : SEARCH_INACTIVE,
      );
      const [view, setView] = useState<View>(
         props.initialView || (HIDE_GRID_VIEW ? View.Column : View.Grid),
      );
      const [loaded, setLoaded] = React.useState<boolean>(false);

      const isUncategorized = (category?: string) => {
         if (!category) {
            return false;
         }
         const categoryTitle = getTitle(category);
         const path = getPath(categoryTitle);
         return !path.length;
      };

      const updateCategory = (category?: string) => {
         if (!category) {
            return;
         }
         const categoryTitle = getTitle(category);
         const path = getPath(categoryTitle);
         updatePath(path, false);
         setView(View.Grid);
      };

      const getTitle = (title: string): string => {
         for (const key in displayTitles) {
            if (displayTitles[key] === title) {
               return key;
            }
         }
         return title;
      };

      const getPath = (key: string): string[] => {
         function recurse(tree: any, key: string): string[] | null {
            for (const i in tree) {
               if (i === key) {
                  return [key];
               } else if (typeof tree[i] === 'object') {
                  const path = recurse(tree[i], key);
                  if (path) {
                     return [i, ...path];
                  }
               }
            }
            return null;
         }
         return recurse(hierarchy, key) || [];
      };

      /**
       * Update path and query state given a path.
       * @param {string[]} path
       * @param {boolean} updateQuery whether or not to update the query state
       */
      const updatePath = (path: string[], updateQuery = true) => {
         const displayTitle = getDisplayTitleFromItemName(
            path[path.length - 1] || '',
         );

         setCategory(displayTitle);

         if (updateQuery) {
            setQuery(displayTitle);
         }

         setSearchState(SEARCH_INACTIVE);
         setPath(path);
      };

      const getDisplayTitleFromItemName = (itemName: string): string => {
         return (displayTitles && displayTitles[itemName]) || itemName;
      };

      const devicePickerContext: DevicePickerContextType = {
         state,
         query,
         category,
         hierarchy,
         displayTitles,
         leaves,
         path,
         searchState,
         view,
         loaded,
         setState,
         setQuery,
         setCategory,
         setPath,
         setSearchState,
         setView,
         setLoaded,
         updateCategory,
         isUncategorized,
      };

      useEffect(() => {
         props
            .fetchHierarchy()
            .then((json) => {
               setHierarchy(json.hierarchy);
               setDisplayTitles(json.display_titles);
               setLeaves(
                  findAllLeaves(
                     json.hierarchy,
                     json.display_titles,
                     new Set<string>(),
                  ),
               );
               setLoaded(true);
            })
            .catch((reason) => {
               throw reason;
            });
      }, []);

      useEffect(() => {
         const loaded = displayTitles && hierarchy;
         if (loaded) {
            updateCategory(category || props.initialDevice);
            setLoaded(true);
         }
      }, [displayTitles, hierarchy]);

      return (
         <DevicePickerContext.Provider value={devicePickerContext}>
            <Component {...props} />
         </DevicePickerContext.Provider>
      );
   };
};
